import BelongManage from '../BelongManage';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NoColumn } from '@components/Common/CTable/components/IdxColumn';
import { TableColumnTypes } from '@typedef/components/Common/CTable/table.column.type';
import CheckBoxContainer from '@components/Common/CInput/containers/CheckBoxContainer';
import { apiRoute, requestSecureDelete, requestSecureGet } from '@libs/api';
import useToken from '@hooks/useToken';
import { BelongDataTypes } from '@typedef/components/BelongManage/belong.data.types';
import { useNavigate } from 'react-router-dom';

const BelongManageContainer = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useToken();
  const [tableData, setTableData] = useState<BelongDataTypes[]>([]);
  const [selectRows, setSelectRows] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 체크박스 선택
  const onChangeCheckBox = useCallback(
    (idx: number) => {
      if (selectRows.includes(idx)) {
        const filter = selectRows.filter((row) => row !== idx);
        return setSelectRows(filter);
      }
      setSelectRows((row) => [...row, idx]);
    },
    [selectRows],
  );

  // 부서 관리 (등록, 수정)
  const manage = useCallback(
    (type: 'insert' | 'update') => {
      let url = `/belong-info?type=${type}`;
      if (type === 'update') {
        if (selectRows.length > 1) {
          alert('수정할 정보를 하나만 선택해 주세요.');
          return;
        } else if (selectRows.length === 0) {
          alert('수정할 정보를 선택해 주세요.');
          return;
        }
        url += `&belongId=${selectRows[0]}`;
      }
      navigate(url);
    },
    [navigate, selectRows],
  );

  // 부서 삭제
  const deleteBelongData = useCallback(async () => {
    if (selectRows.length === 0) {
      alert('삭제할 데이터를 선택해 주세요.');
      return;
    }
    if (
      window.confirm(
        '해당 부서와 관련된 모든 정보가 사라집니다.\n삭제하시겠습니까?',
      )
    ) {
      await Promise.all(
        selectRows.map(
          (idx) =>
            new Promise((resolve, reject) => {
              requestSecureDelete(
                apiRoute.belong.deleteBelong + idx + 'belong=true',
                {},
                getAccessToken()!,
              ).then((data) => {
                if (data.config.status >= 200 && data.config.status < 400) {
                  resolve(data);
                } else {
                  reject('http request failed with ' + data.config.status);
                }
              });
            }),
        ),
      )
        .then(() => {
          alert('성공적으로 삭제가 완료되었습니다.');
          window.location.reload();
        })
        .catch(() => {
          alert('삭제 실패');
        });
    }
  }, [getAccessToken, selectRows]);

  // 테이블 검색
  const searchTable = useCallback(() => {
    if (inputRef.current) {
      setSearchValue(inputRef.current.value);
    }
  }, []);

  // 테이블 검색내역 삭제
  const clearSearchValue = useCallback(() => {
    const { current } = inputRef;
    if (current) {
      current.value = '';
    }
    setSearchValue(null);
  }, []);

  const columns: TableColumnTypes[] = useMemo(
    () => [
      {
        Header: '',
        accessor: 'checkbox',
        disableSortBy: true,
        width: 50,
        Cell: ({ row }: { row: any }) => {
          return (
            <CheckBoxContainer
              id={row.original.store.idx}
              defaultChecked={selectRows.includes(row.original.store.idx)}
              onChangeFunc={onChangeCheckBox}
            />
          );
        },
      },
      NoColumn('idx'),
      {
        Header: '부서명',
        accessor: 'store.name',
      },
      {
        Header: '대표전화',
        accessor: 'store.phone',
      },
      {
        Header: '아이디',
        accessor: 'store.manager_id',
      },
      {
        Header: '담당자',
        accessor: 'store.manager_name',
      },
      {
        Header: '담당자 연락처',
        accessor: 'store.manager_phone',
      },
    ],
    [onChangeCheckBox, selectRows],
  );

  const getTableData = useCallback(async () => {
    const { config, data } = await requestSecureGet<BelongDataTypes[]>(
      apiRoute.belong.getBelongs,
      {},
      getAccessToken()!,
    );
    if (config.status >= 200 && config.status < 400) setTableData(data);
  }, [getAccessToken]);

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  return (
    <BelongManage
      columns={columns}
      tableData={tableData}
      manage={manage}
      deleteBelongData={deleteBelongData}
      inputRef={inputRef}
      searchValue={searchValue}
      searchTable={searchTable}
      clearSearchValue={clearSearchValue}
    />
  );
};

export default BelongManageContainer;
